import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import ImportarRazaoService, { AnaliseLayout, ArquivoAnalise } from "../../../services/ImportarRazaoService";

interface Props {
  uploadId: number;
  empresaNome?: string;
  onConfirmado: () => void;
  onCancelado: () => void;
  onClose: () => void;
}

const usd = (v: number) =>
  `US$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;

const situacaoTexto = (a: ArquivoAnalise) => {
  switch (a.situacao) {
    case "TEMPLATE": return { texto: "Template Ainumera", cor: "#64748B" };
    case "CONHECIDO": return { texto: "Já conhecido pela empresa", cor: "#22C55E" };
    case "MESMO_FORMATO": return { texto: `Mesmo formato ${a.grupoId}`, cor: "#64748B" };
    default: return { texto: `Formato novo ${a.grupoId}`, cor: "#0EA5E9" };
  }
};

const custoTexto = (a: ArquivoAnalise) => {
  if (a.situacao === "NOVO") return usd(a.custoEstimadoUsd);
  if (a.situacao === "MESMO_FORMATO") return "desconsiderado";
  return usd(0);
};

/**
 * DEVN3-602: antes de chamar a IA, mostra quanto vai custar identificar os formatos novos da leva —
 * por arquivo, com os do mesmo formato desconsiderados (1 chamada por formato) e os já conhecidos a
 * custo zero. Nada é enviado ao Claude sem a confirmação.
 */
const ModalEstimativaIA = ({ uploadId, empresaNome, onConfirmado, onCancelado, onClose }: Props) => {
  const [analise, setAnalise] = useState<AnaliseLayout | null>(null);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    ImportarRazaoService.analise(uploadId)
      .then(setAnalise)
      .catch(() => Swal.fire({ icon: "error", text: "Não foi possível carregar a estimativa." }));
  }, [uploadId]);

  async function confirmar() {
    setEnviando(true);
    try {
      await ImportarRazaoService.confirmarIa(uploadId);
      onConfirmado();
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível iniciar a identificação." });
    } finally {
      setEnviando(false);
    }
  }

  async function cancelar() {
    const r = await Swal.fire({
      icon: "question",
      text: "Cancelar esta importação? Nada será processado.",
      showCancelButton: true,
      confirmButtonText: "Sim, cancelar",
      cancelButtonText: "Voltar",
    });
    if (!r.isConfirmed) return;
    setEnviando(true);
    try {
      await ImportarRazaoService.cancelar(uploadId);
      onCancelado();
    } catch (err: any) {
      Swal.fire({ icon: "error", text: err?.message || "Não foi possível cancelar." });
    } finally {
      setEnviando(false);
    }
  }

  const aguardando = analise?.statusLayout === "AGUARDANDO_CONFIRMACAO_IA";
  const tokensTotais = (analise?.grupos ?? []).reduce(
    (s, g) => s + g.tokensInputEstimados + g.tokensOutputEstimados, 0);

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.4)", zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)",
      }}
      onClick={onClose}
    >
      <div
        className="card shadow-lg"
        style={{ width: 720, maxWidth: "94vw", borderRadius: 16, border: "none" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-header d-flex justify-content-between align-items-center py-4 px-5" style={{ background: "#F8FAFC", borderRadius: "16px 16px 0 0" }}>
          <div>
            <h3 className="card-title fw-bolder text-dark m-0" style={{ fontSize: 18 }}>
              Identificar o layout com IA
            </h3>
            {empresaNome && <div className="text-muted fs-7">{empresaNome}</div>}
          </div>
          <button className="btn btn-icon btn-sm btn-active-light-primary" onClick={onClose}>
            <i className="bi bi-x fs-2" />
          </button>
        </div>

        <div className="card-body p-5">
          {!analise ? (
            <div className="text-muted">Carregando a estimativa...</div>
          ) : (
            <>
              <p className="mb-4" style={{ fontSize: 13 }}>
                {analise.chamadasEstimadas === 1
                  ? "Encontramos 1 formato de arquivo que ainda não conhecemos para esta empresa."
                  : `Encontramos ${analise.chamadasEstimadas} formatos de arquivo que ainda não conhecemos para esta empresa.`}{" "}
                A IA recebe só uma amostra de cada formato para identificar as colunas; depois o sistema
                confere os saldos do próprio arquivo antes de importar. Na próxima vez, esse formato entra
                direto, sem custo.
              </p>

              <table className="table table-sm table-row-dashed align-middle mb-4" style={{ fontSize: 12 }}>
                <thead>
                  <tr className="text-muted fw-bold">
                    <th>Arquivo</th>
                    <th>Situação</th>
                    <th className="text-end">Custo estimado</th>
                  </tr>
                </thead>
                <tbody>
                  {analise.arquivos.map((a, i) => {
                    const s = situacaoTexto(a);
                    return (
                      <tr key={i}>
                        <td className="text-truncate" style={{ maxWidth: 300 }} title={a.nome}>{a.nome}</td>
                        <td style={{ color: s.cor, fontWeight: 600 }}>{s.texto}</td>
                        <td className="text-end" style={{ color: a.situacao === "NOVO" ? "#1E293B" : "#94A3B8" }}>
                          {custoTexto(a)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="fw-bolder">
                    <td>Total</td>
                    <td>
                      {analise.chamadasEstimadas} chamada(s) à IA · ~{tokensTotais.toLocaleString("pt-BR")} tokens
                    </td>
                    <td className="text-end">{usd(analise.custoEstimadoUsd)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="text-muted" style={{ fontSize: 11 }}>
                Modelo {analise.modelo || "Claude"}. Se a conferência de algum formato não fechar, a IA
                faz 1 nova tentativa com o mesmo custo aproximado (máximo {usd(analise.custoEstimadoUsd * 2)}).
                Se ainda assim não fechar, você ajusta o mapeamento das colunas na tela, sem custo.
              </div>

              {!aguardando && (
                <div className="alert alert-light mt-4 mb-0" style={{ fontSize: 12 }}>
                  Esta importação não está mais aguardando confirmação.
                </div>
              )}
            </>
          )}
        </div>

        <div className="card-footer d-flex justify-content-between py-4 px-5 bg-light-soft" style={{ borderRadius: "0 0 16px 16px" }}>
          <button className="btn btn-sm btn-light-danger px-4" onClick={cancelar} disabled={enviando || !aguardando}>
            Cancelar importação
          </button>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-secondary px-4" onClick={onClose}>Decidir depois</button>
            <button className="btn btn-sm btn-primary px-4" onClick={confirmar} disabled={enviando || !aguardando}>
              {enviando ? "Enviando..." : "Analisar com IA"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalEstimativaIA;
